import { useAuthorizedQuery } from "../api/authorized-query";
import { seedgreenAPI } from "../api/axios";
import { STALE_TIME_MEDIUM } from "../queries/query.constants";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { computed } from "vue";
export function useTaskRequests(options = {}) {
    const isAuthorized = useAuthorizedQuery();
    const queryKey = computed(() => [
        "task-requests",
        options.requestStatus?.value,
        options.requestingUserId?.value,
    ]);
    const query = useQuery({
        enabled: computed(() => isAuthorized.value && (options.enabled?.value ?? true)),
        queryKey,
        queryFn: async () => {
            const response = await seedgreenAPI.get("v2/TaskRequest", {
                params: {
                    requestStatus: options.requestStatus?.value,
                    requestingUserId: options.requestingUserId?.value,
                },
            });
            return response.data;
        },
        staleTime: STALE_TIME_MEDIUM,
    });
    return {
        taskRequests: query.data,
        ...query,
    };
}
export function useTaskRequest(id) {
    const queryClient = useQueryClient();
    const isAuthorized = useAuthorizedQuery();
    const query = useQuery({
        enabled: computed(() => isAuthorized.value && !!id.value),
        queryKey: ["task-request", id],
        queryFn: async () => {
            if (!id.value)
                return null;
            const response = await seedgreenAPI.get(`v2/TaskRequest/${id.value}`);
            return response.data;
        },
        staleTime: STALE_TIME_MEDIUM,
        initialData: () => {
            const queries = queryClient.getQueriesData({
                queryKey: ["task-requests"],
            });
            for (const [_, value] of queries) {
                if (Array.isArray(value)) {
                    const found = value.find((tr) => tr.id === id.value);
                    if (found)
                        return found;
                }
            }
        },
    });
    return {
        taskRequest: query.data,
        ...query,
    };
}
export function useSaveTaskRequest() {
    const queryClient = useQueryClient();
    const isAuthorized = useAuthorizedQuery();
    const { mutateAsync, status, error } = useMutation({
        mutationFn: async (taskRequest) => {
            if (!isAuthorized.value)
                return;
            const response = await seedgreenAPI.put("v2/TaskRequest", taskRequest);
            return response.data;
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({
                queryKey: ["task-requests"],
            });
            // Invalidate plantings queries to ensure planting data gets refetched
            // This ensures the planting name shows up immediately for the new task request
            if (variables.plantingId) {
                queryClient.invalidateQueries({
                    queryKey: ["plantings", "byIds"],
                });
            }
        },
    });
    return {
        saveTaskRequest: mutateAsync,
        status,
        error,
    };
}
/**
 * Mutation for accepting a task request.
 * Creates the associated task and updates the request status.
 */
export function useAcceptTaskRequest() {
    const queryClient = useQueryClient();
    const isAuthorized = useAuthorizedQuery();
    const { mutateAsync, status, error } = useMutation({
        mutationFn: async (params) => {
            if (!isAuthorized.value)
                return;
            const response = await seedgreenAPI.post(`v2/TaskRequest/${params.id}/accept`, params.dto);
            return response.data;
        },
        onSuccess: () => {
            // Invalidate task requests so the list updates
            queryClient.invalidateQueries({
                queryKey: ["task-requests"],
            });
            // Invalidate tasks since a new task was created
            queryClient.invalidateQueries({
                queryKey: ["tasks"],
            });
        },
    });
    return {
        acceptTaskRequest: mutateAsync,
        status,
        error,
    };
}
/**
 * Mutation for denying a task request.
 * Updates the request status to Denied.
 */
export function useDenyTaskRequest() {
    const queryClient = useQueryClient();
    const isAuthorized = useAuthorizedQuery();
    const { mutateAsync, status, error } = useMutation({
        mutationFn: async (params) => {
            if (!isAuthorized.value)
                return;
            await seedgreenAPI.post(`v2/TaskRequest/${params.id}/deny`, params.dto);
        },
        onSuccess: () => {
            // Invalidate task requests so the list updates
            queryClient.invalidateQueries({
                queryKey: ["task-requests"],
            });
        },
    });
    return {
        denyTaskRequest: mutateAsync,
        status,
        error,
    };
}
